// services/notificationService.js - Handles SSE notifications connection

class NotificationService {
  constructor() {
    this.eventSource = null;
    this.listeners = [];
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
  }

  /**
   * Connect to SSE notification stream
   * @param {string} userId - Current user ID/email
   * @param {Function} onNotification - Callback when notification received
   * @param {Function} onError - Callback on error
   */
  connect(userId, onNotification, onError) {
    if (!userId) {
      console.error('NotificationService: userId is required');
      return;
    }

    if (this.eventSource) {
      this.disconnect();
    }

    try {
      const apiUrl = window.location.hostname === 'localhost'
        ? (process.env.REACT_APP_API_URL || 'http://localhost:5000')
        : '';
      const streamUrl = `${apiUrl}/api/notifications/stream?userId=${encodeURIComponent(userId)}`;
      
      this.eventSource = new EventSource(streamUrl, { withCredentials: true });

      this.eventSource.onopen = () => {
        console.log('[Notifications] Connected to notification stream');
        this.reconnectAttempts = 0;
      };

      this.eventSource.onmessage = (event) => {
        try {
          const notification = JSON.parse(event.data);
          console.log('[Notifications] Received:', notification);
          onNotification(notification);
        } catch (error) {
          console.error('[Notifications] Error parsing message:', error);
        }
      };

      this.eventSource.onerror = (error) => {
        console.error('[Notifications] Stream error:', error);
        this.handleError(error, onError, userId, onNotification);
      };

    } catch (error) {
      console.error('[Notifications] Connection error:', error);
      onError?.(error);
    }
  }

  /**
   * Handle reconnection logic
   */
  handleError(error, onError, userId, onNotification) {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }

    onError?.(error);

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`[Notifications] Reconnecting in ${this.reconnectDelay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
      
      setTimeout(() => {
        this.connect(userId, onNotification, onError);
      }, this.reconnectDelay);
    }
  }

  /**
   * Disconnect from notification stream
   */
  disconnect() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
      console.log('[Notifications] Disconnected from stream');
    }
  }

  /**
   * Check if connected
   */
  isConnected() {
    return this.eventSource && this.eventSource.readyState === EventSource.OPEN;
  }
}

// Export singleton instance
export default new NotificationService();
